import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

import { Trans } from 'react-i18next';

import { BevelWindow } from '@/components/prototype/bevel-window';

interface PanelErrorBoundaryProps {
  /** Title bar of the replacement panel. */
  title: string;
  children: ReactNode;
}

interface PanelErrorBoundaryState {
  hasError: boolean;
}

/**
 * Keeps a render error inside one optional panel (e.g. the Discord mirror)
 * from unmounting the whole SPA. React still only offers this as a class
 * component. The panel is replaced by a short notice in its own window.
 */
export class PanelErrorBoundary extends Component<
  PanelErrorBoundaryProps,
  PanelErrorBoundaryState
> {
  state: PanelErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): PanelErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface it for the console/error tracker; the UI already degraded.
    console.error(`Panel "${this.props.title}" failed to render`, error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <BevelWindow title={this.props.title} shadow="sm">
          <p className="text-destructive font-mono text-sm" role="alert">
            <Trans i18nKey="state.panelFailed" ns="common" />
          </p>
        </BevelWindow>
      );
    }
    return this.props.children;
  }
}
